import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Disc3, ShieldAlert, Lock, Eye, Pencil, Check, X, Trash2, Archive, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/services/api';
import { isValidPhone, normalizePhone, PHONE_ERROR } from '@/lib/phone';
import AdminDJGallery from '@/components/AdminDJGallery';

// Stored status -> UI tab. We DISPLAY "approved" as "Accepted"; the stored value stays "approved".
const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Accepted' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'removed', label: 'Removed' },
  { key: 'archived', label: 'Archived' },
];
const STATUS_LABEL = { pending: 'Pending', approved: 'Accepted', rejected: 'Rejected', removed: 'Removed', archived: 'Archived' };

function Centered({ children }) {
  return <div className="max-w-4xl mx-auto px-4 py-16 text-center opacity-70">{children}</div>;
}
function GateLogin() {
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <Lock className="mx-auto mb-4 h-10 w-10 opacity-60" />
      <p className="mb-4">Please log in.</p>
      <Link to="/login"><Button>Go to Login</Button></Link>
    </div>
  );
}
function GateAdmin({ role }) {
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <ShieldAlert className="mx-auto mb-4 h-10 w-10 opacity-60" />
      <h1 className="text-xl font-semibold mb-2">Admin access required</h1>
      <p className="opacity-80">Your role is <span className="capitalize font-medium">{role || 'unknown'}</span>.</p>
    </div>
  );
}

// DJ images can be gated (visible only to owner/admin until the app is public), so we
// can't put the path straight in <img src> — it would send no Authorization header.
// Blob-fetch with the Bearer token and hand back an object URL, revoked on unmount.
function AuthImage({ src, alt, className }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoked = null;
    let alive = true;
    setUrl(null); setFailed(false);
    if (!src) { setFailed(true); return; }
    api.fetchAuthedBlobUrl(src)
      .then((u) => { if (alive) { revoked = u; setUrl(u); } else { URL.revokeObjectURL(u); } })
      .catch(() => alive && setFailed(true));
    return () => { alive = false; if (revoked) URL.revokeObjectURL(revoked); };
  }, [src]);
  if (failed) return <div className={`${className} flex items-center justify-center bg-muted text-xs opacity-50`}>No image</div>;
  if (!url) return <div className={`${className} flex items-center justify-center bg-muted text-xs opacity-50`}>Loading…</div>;
  return <img src={url} alt={alt} className={className} />;
}

function Field({ label, children }) {
  return <div><span className="opacity-60">{label}: </span><span>{children ?? '—'}</span></div>;
}

const EMPTY_EDIT = { dj_name: '', real_name: '', phone: '', public_phone: false, booking_email: '', city: '', genres: '', bio: '', instagram: '', profile_picture: '', portfolio_url: '' };

export default function AdminDJs() {
  const [authed] = useState(api.isAuthenticated());
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);   // full admin view of one DJ
  const [preview, setPreview] = useState(null);      // public-shape preview
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState(EMPTY_EDIT);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const detailRef = useRef(null);

  const fetchRows = useCallback(async (status) => {
    try { setRows(await api.get(`/admin/djs?status=${status}`)); }
    catch (e) { toast.error(e.message || 'Failed to load DJ applications'); }
  }, []);

  useEffect(() => {
    if (!authed) { setLoading(false); return; }
    api.get('/users/me').then((me) => setRole(me.role)).catch(() => {}).finally(() => setLoading(false));
  }, [authed]);

  useEffect(() => { if (role === 'admin') fetchRows(tab); }, [role, tab, fetchRows]);

  const closeDetail = () => { setSelected(null); setPreview(null); setEditing(false); setRejecting(false); };

  const openDetail = async (id) => {
    setPreview(null); setEditing(false); setRejecting(false); setRejectReason('');
    try {
      const full = await api.get(`/admin/djs/${id}`);
      setSelected(full);
      setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (e) { toast.error(e.message || 'Failed to load detail'); }
  };

  const refreshAfter = async (id) => { await fetchRows(tab); if (id) { try { setSelected(await api.get(`/admin/djs/${id}`)); } catch { closeDetail(); } } };

  const doPreview = async (id) => {
    try { setPreview(await api.get(`/admin/djs/${id}/preview`)); }
    catch (e) { toast.error(e.message || 'Preview failed'); }
  };

  const startEdit = (d) => {
    setEdit({
      dj_name: d.dj_name || '', real_name: d.real_name || '', phone: d.phone || '',
      public_phone: !!d.public_phone, booking_email: d.booking_email || '', city: d.city || '',
      genres: (d.genres || []).join(', '), bio: d.bio || '', instagram: d.instagram || '',
      profile_picture: d.profile_picture || '', portfolio_url: d.portfolio_url || '',
    });
    setEditing(true); setPreview(null); setRejecting(false);
  };

  const saveEdit = async () => {
    if (!isValidPhone(edit.phone)) { toast.error(PHONE_ERROR); return; }
    setBusy(true);
    try {
      const payload = {
        dj_name: edit.dj_name, real_name: edit.real_name, phone: normalizePhone(edit.phone),
        public_phone: edit.public_phone, city: edit.city, bio: edit.bio,
        instagram: edit.instagram.trim() || null,
        booking_email: edit.booking_email.trim() || null,
        portfolio_url: edit.portfolio_url.trim() || null,
        genres: edit.genres.split(',').map((g) => g.trim()).filter(Boolean),
      };
      await api.patch(`/admin/djs/${selected.application_id}`, payload);
      toast.success('Changes saved' + (selected.status === 'approved' ? ' — live now' : ''));
      setEditing(false);
      await refreshAfter(selected.application_id);
    } catch (e) { toast.error(e.message || 'Save failed'); }
    finally { setBusy(false); }
  };

  const act = async (path, okMsg, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await api.post(`/admin/djs/${selected.application_id}/${path}`, {});
      toast.success(okMsg);
      // Status changed -> the record leaves the current tab; close and refresh list.
      await fetchRows(tab);
      closeDetail();
    } catch (e) { toast.error(e.message || 'Action failed'); }
    finally { setBusy(false); }
  };

  const submitReject = async () => {
    if (!rejectReason.trim()) { toast.error('A rejection reason is required.'); return; }
    setBusy(true);
    try {
      await api.post(`/admin/djs/${selected.application_id}/reject`, { rejection_reason: rejectReason.trim() });
      toast.success('Application rejected');
      await fetchRows(tab);
      closeDetail();
    } catch (e) { toast.error(e.message || 'Reject failed'); }
    finally { setBusy(false); }
  };

  if (!authed) return <GateLogin />;
  if (loading) return <Centered>Loading…</Centered>;
  if (role !== 'admin') return <GateAdmin role={role} />;

  const s = selected;
  const warn = s && (s.previous_email_warning || s.previous_name_warning);

  return (
    <div className="max-w-5xl mx-auto px-4 pt-20 pb-24 md:pb-8">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-4"><Disc3 className="h-6 w-6" /> Admin · DJs</h1>

      <div className="flex flex-wrap gap-2 mb-5 border-b border-border/50 pb-3">
        {TABS.map((t) => (
          <Button key={t.key} variant={tab === t.key ? 'default' : 'outline'} size="sm"
            onClick={() => { closeDetail(); setTab(t.key); }}>{t.label}</Button>
        ))}
      </div>

      {/* ---- Detail / workspace panel (one DJ at a time) ---- */}
      {s && (
        <Card ref={detailRef} className="mb-6 border-primary/40">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              {s.dj_name} <Badge variant="outline">{STATUS_LABEL[s.status] || s.status}</Badge>
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={closeDetail}>Close</Button>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="flex flex-col sm:flex-row gap-4">
              <AuthImage src={s.profile_picture} alt={s.dj_name} className="w-40 h-40 rounded-lg object-cover shrink-0" />
              <div className="space-y-1 flex-1 min-w-0">
                <Field label="Real name">{s.real_name}</Field>
                <Field label="Account email">{s.account_email}</Field>
                <Field label="Contact email">{s.email}</Field>
                <Field label="Phone">{s.phone} {s.public_phone ? <Badge className="ml-1 bg-green-600">public</Badge> : <Badge variant="outline" className="ml-1">private</Badge>}</Field>
                <Field label="Booking email">{s.booking_email}</Field>
                <Field label="City">{s.city}</Field>
                <Field label="Genres">{(s.genres || []).join(', ')}</Field>
                <Field label="Instagram">{s.instagram ? <a className="text-primary underline" href={s.instagram} target="_blank" rel="noreferrer">{s.instagram}</a> : '—'}</Field>
                <Field label="Slug">{s.slug}</Field>
                <Field label="Agreement accepted">{s.agreement_accepted ? `Yes (${(s.agreement_accepted_at || '').slice(0, 10)})` : 'No'}</Field>
              </div>
            </div>

            <div><span className="opacity-60">Bio:</span><p className="mt-1 whitespace-pre-wrap">{s.bio}</p></div>

            {warn && (
              <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-amber-700 text-xs space-y-1">
                {s.previous_email_warning && <div>⚠ {s.previous_email_warning} ({s.previous_email_application_count} prior)</div>}
                {s.previous_name_warning && <div>⚠ {s.previous_name_warning} ({s.previous_name_application_count} prior)</div>}
              </div>
            )}

            {s.status === 'rejected' && (
              <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-xs">
                <b>Rejection reason:</b> {s.rejection_reason || '—'}
                {s.rejected_by && <span className="opacity-70"> · by {s.rejected_by} {(s.rejected_at || '').slice(0, 10)}</span>}
              </div>
            )}

            <div className="text-xs opacity-60 space-y-0.5">
              <div>Submitted: {(s.submitted_at || '').slice(0, 19).replace('T', ' ')}{s.last_edited_by ? ` · last edited by ${s.last_edited_by}` : ''}</div>
              {s.reviewed_by && <div>Reviewed by {s.reviewed_by}</div>}
              {s.removed_at && <div>Removed {(s.removed_at || '').slice(0, 10)} by {s.removed_by}</div>}
              {s.archived_at && <div>Archived {(s.archived_at || '').slice(0, 10)} by {s.archived_by}</div>}
              {s.restored_at && <div>Restored {(s.restored_at || '').slice(0, 10)} by {s.restored_by}</div>}
              <div>application_id: {s.application_id}</div>
            </div>

            {/* ---- Gallery moderation (preview + soft-remove/restore) ---- */}
            <AdminDJGallery djId={s.application_id} />

            {/* ---- Preview (public shape) ---- */}
            {preview && (
              <div className="rounded-md border border-primary/40 p-3">
                <div className="flex items-center justify-between mb-2"><b>Public preview</b><Button variant="ghost" size="sm" onClick={() => setPreview(null)}>Hide</Button></div>
                <div className="text-xs space-y-1">
                  <Field label="DJ name">{preview.dj_name}</Field>
                  <Field label="City">{preview.city}</Field>
                  <Field label="Genres">{(preview.genres || []).join(', ')}</Field>
                  <Field label="Phone (public)">{'phone' in preview ? preview.phone : 'hidden'}</Field>
                  <Field label="Booking email">{preview.booking_email}</Field>
                  <Field label="Instagram">{preview.instagram}</Field>
                  <div className="opacity-60 mt-1">real name / account email / contact phone are never shown publicly.</div>
                </div>
              </div>
            )}

            {/* ---- Edit form ---- */}
            {editing && (
              <div className="rounded-md border border-primary/40 p-3 space-y-3">
                <b>Edit application{s.status === 'approved' ? ' (changes go live immediately)' : ''}</b>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>DJ name</Label><Input value={edit.dj_name} onChange={(e) => setEdit({ ...edit, dj_name: e.target.value })} /></div>
                  <div><Label>Real name</Label><Input value={edit.real_name} onChange={(e) => setEdit({ ...edit, real_name: e.target.value })} /></div>
                  <div><Label>City</Label><Input value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} /></div>
                  <div><Label>Phone</Label><Input value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></div>
                  <div><Label>Booking email</Label><Input value={edit.booking_email} onChange={(e) => setEdit({ ...edit, booking_email: e.target.value })} /></div>
                  <div><Label>Instagram (https://)</Label><Input value={edit.instagram} onChange={(e) => setEdit({ ...edit, instagram: e.target.value })} /></div>
                  <div><Label>Portfolio link (http/https)</Label><Input value={edit.portfolio_url} onChange={(e) => setEdit({ ...edit, portfolio_url: e.target.value })} placeholder="https://…" /></div>
                  <div className="sm:col-span-2"><Label>Genres (comma separated)</Label><Input value={edit.genres} onChange={(e) => setEdit({ ...edit, genres: e.target.value })} /></div>
                  <div className="sm:col-span-2"><Label>Bio</Label><Textarea rows={4} value={edit.bio} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} /></div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={edit.public_phone} onChange={(e) => setEdit({ ...edit, public_phone: e.target.checked })} /> Show phone publicly</label>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" disabled={busy} onClick={saveEdit}>Save</Button>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              </div>
            )}

            {/* ---- Reject form ---- */}
            {rejecting && (
              <div className="rounded-md border border-red-500/40 p-3 space-y-2">
                <Label>Rejection reason (required)</Label>
                <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Why is this application being rejected?" />
                <div className="flex gap-2">
                  <Button size="sm" variant="destructive" disabled={busy} onClick={submitReject}>Confirm rejection</Button>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => setRejecting(false)}>Cancel</Button>
                </div>
              </div>
            )}

            {/* ---- Action bar (per status) ---- */}
            {!editing && !rejecting && (
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => doPreview(s.application_id)}><Eye className="h-4 w-4 mr-1" /> Preview</Button>
                {(s.status === 'pending' || s.status === 'approved') && (
                  <Button size="sm" variant="outline" onClick={() => startEdit(s)}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
                )}
                {s.status === 'pending' && (
                  <>
                    <Button size="sm" disabled={busy} onClick={() => act('approve', 'Application accepted — DJ is live')}><Check className="h-4 w-4 mr-1" /> Accept</Button>
                    <Button size="sm" variant="destructive" onClick={() => setRejecting(true)}><X className="h-4 w-4 mr-1" /> Reject</Button>
                  </>
                )}
                {s.status === 'approved' && (
                  <Button size="sm" variant="destructive" disabled={busy} onClick={() => act('remove', 'DJ removed from public directory', `Remove "${s.dj_name}" from the public directory? The user account stays active.`)}><Trash2 className="h-4 w-4 mr-1" /> Remove</Button>
                )}
                {s.status === 'removed' && (
                  <>
                    <Button size="sm" disabled={busy} onClick={() => act('restore', 'DJ restored — live again')}><RotateCcw className="h-4 w-4 mr-1" /> Restore</Button>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => act('archive', 'Application archived', `Archive "${s.dj_name}"?`)}><Archive className="h-4 w-4 mr-1" /> Archive</Button>
                  </>
                )}
                {s.status === 'archived' && (
                  <Button size="sm" disabled={busy} onClick={() => act('restore', 'DJ restored — live again')}><RotateCcw className="h-4 w-4 mr-1" /> Restore</Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---- List for the active tab ---- */}
      <div className="space-y-2">
        {rows.length === 0 && <p className="opacity-60 text-center py-10">No {STATUS_LABEL[tab]?.toLowerCase()} applications.</p>}
        {rows.map((d) => (
          <Card key={d.application_id} className={selected?.application_id === d.application_id ? 'border-primary' : ''}>
            <CardContent className="py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <AuthImage src={d.profile_picture} alt={d.dj_name} className="w-12 h-12 rounded-md object-cover shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium truncate">{d.dj_name}</div>
                  <div className="text-sm opacity-70 truncate">{d.city} · {d.account_email}</div>
                  {tab === 'rejected' && d.rejection_reason && <div className="text-xs text-red-600 truncate">Reason: {d.rejection_reason}</div>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(d.previous_email_warning || d.previous_name_warning) && <Badge variant="outline" className="border-amber-500 text-amber-600">history</Badge>}
                <Button variant="outline" size="sm" onClick={() => openDetail(d.application_id)}>Open</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
